/**
 * The template's ROW title, editable in the editor header — the catalogue handle a tutor picks the
 * assessment by ("Baseline Math - Grade 4"). The store has held it as editable state since the
 * editor was built, and `setTitle` with it; until RND-53 nothing in the UI called either, so a
 * template could only be named at creation. This hook is that missing one-line delegation (B9).
 *
 * NOT `definition.title`, the kid-facing name on the intro screen — `IntroEditor` owns that one, and
 * the two are meant to diverge the moment either is retitled.
 *
 * Nothing is trimmed or clamped WHILE TYPING: a trailing space is legal to type mid-word, and a
 * clamp would silently swallow the 256th keystroke. `refMode` paints the field instead, and the
 * editor page's `save()` is what trims and refuses.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { InputPrimaryMode } from '@shared/ui/fields'

import { useAssessmentEditorStore } from './store'
import { isTemplateTitleValid } from './assessment-template-options'

interface UseTemplateTitleReturn {
  refTitle: ComputedRef<string>
  /** `'error'` the moment the field holds something the API would reject, so the author sees it
   *  before pressing Save rather than in a toast afterwards. */
  refMode: ComputedRef<InputPrimaryMode>
  setTitle: (value: string) => void
}

export const useTemplateTitle = (): UseTemplateTitleReturn => {
  const store = useAssessmentEditorStore()

  const refTitle = computed<string>(() => store.getTitle)

  const refMode = computed<InputPrimaryMode>(() =>
    isTemplateTitleValid(store.getTitle) ? 'normal' : 'error')

  const setTitle = (value: string): void => {
    store.setTitle(value)
  }

  return { refTitle, refMode, setTitle }
}
